<template>
    <div class="subscription-manager">
      <!-- Menu Trigger -->
      <v-menu offset-y content-class="modern-menu">
        <template v-slot:activator="{ on, attrs }">
          <div class="account-control">
            <v-btn
              icon
              v-bind="attrs"
              v-on="on"
              class="control-btn"
            >
              <v-icon v-if="!currentUser.loggedIn">
                mdi-incognito
              </v-icon>
              <v-icon v-else>
                mdi-account-circle-outline
              </v-icon>
            </v-btn>
            <div class="status-badges" v-if="user.is_pro || user.is_pro_team">
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <span class="status-badge" v-on="on" v-if="user.is_pro && !user.is_pro_team">
                    <v-icon small color="success">mdi-star-three-points-outline</v-icon>
                  </span>
                </template>
                <span>Pro Member</span>
              </v-tooltip>
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <span class="status-badge" v-on="on" v-if="user.is_pro_team">
                    <v-icon small color="success">mdi-account-group</v-icon>
                  </span>
                </template>
                <span>Pro Team Member</span>
              </v-tooltip>
            </div>
          </div>
        </template>

        <!-- Menu Content -->
        <v-list>
          <UserInfoSection
            :user="user"
            :current-user="currentUser"
            :current-galaxy-name="currentGalaxyName"
          />

          <v-divider></v-divider>

          <SubscriptionActions
            :user="user"
            :current-user="currentUser"
            :loading="subscriptionLoading"
            @upgrade="showUpgradeConfirmation"
            @cancel="showCancelConfirmation"
            @reactivate="showReactivationConfirmation"
            @login="handleLogin"
            @logout="handleLogout"
            @navigate-to-members="navigateToMembers"
          />
        </v-list>
      </v-menu>

      <!-- Dialogs -->
      <UpgradeDialog
        v-model="showUpgradeDialog"
        :selected-plan="selectedPlan"
        :loading="subscriptionLoading"
        @confirm="confirmUpgrade"
      />

      <ReactivationDialog
        v-model="showReactivationDialog"
        :reactivation-plan="reactivationPlan"
        :subscription-end-date="user.subscription_end_date"
        :loading="subscriptionLoading"
        @update:plan="reactivationPlan = $event"
        @confirm="confirmReactivation"
      />

      <CancellationDialog
        v-model="showCancelDialog"
        :user="user"
        :loading="cancelLoading"
        @confirm="confirmCancelSubscription"
      />
    </div>
  </template>

  <script>
  import UserInfoSection from './subscription/UserInfoSection.vue';
  import SubscriptionActions from './subscription/SubscriptionActions.vue';
  import UpgradeDialog from './subscription/UpgradeDialog.vue';
  import ReactivationDialog from './subscription/ReactivationDialog.vue';
  import CancellationDialog from './subscription/CancellationDialog.vue';

  export default {
    name: 'SubscriptionManager',

    components: {
      UserInfoSection,
      SubscriptionActions,
      UpgradeDialog,
      ReactivationDialog,
      CancellationDialog,
    },

    props: {
      user: {
        type: Object,
        required: true,
      },
      currentUser: {
        type: Object,
        required: true,
      },
      currentGalaxyName: {
        type: String,
        default: '',
      },
    },

    data() {
      return {
        showUpgradeDialog: false,
        showReactivationDialog: false,
        showCancelDialog: false,
        selectedPlan: null,
        reactivationPlan: 'pro',
        subscriptionLoading: false,
        cancelLoading: false,
      };
    },

    methods: {
      showUpgradeConfirmation(plan) {
        this.selectedPlan = plan;
        this.showUpgradeDialog = true;
      },

      confirmUpgrade() {
        this.showUpgradeDialog = false;
        this.upgradeSubscription(this.selectedPlan);
      },

      showReactivationConfirmation() {
        // Set default plan based on previous subscription
        if (this.user.last_active_subscription === 'team_pro') {
          this.reactivationPlan = 'team_pro';
        } else {
          this.reactivationPlan = 'pro';
        }
        this.showReactivationDialog = true;
      },

      confirmReactivation() {
        this.showReactivationDialog = false;
        this.upgradeSubscription(this.reactivationPlan);
      },

      showCancelConfirmation() {
        this.showCancelDialog = true;
      },

      async confirmCancelSubscription() {
        this.cancelLoading = true;
        try {
          await this.$store.dispatch('websocket/cancelSubscription');
          this.showCancelDialog = false;
          this.$emit('subscription-updated');
        } catch (error) {
          console.error('Error canceling subscription:', error);
          this.$store.dispatch('websocket/showTempMessageFn', {
            message: 'Failed to cancel subscription. Please try again.',
            type: 'error',
          });
        } finally {
          this.cancelLoading = false;
        }
      },

      async upgradeSubscription(planType) {
        this.subscriptionLoading = true;
        try {
          await this.$store.dispatch('websocket/upgradePlan', planType);
          this.$emit('subscription-updated');
        } catch (error) {
          console.error(`Error upgrading to ${planType}:`, error);
          this.$store.dispatch('websocket/showTempMessageFn', {
            message: `Failed to upgrade to ${planType}. Please try again.`,
            type: 'error',
          });
        } finally {
          this.subscriptionLoading = false;
        }
      },

      handleLogin(method) {
        this.$emit('login', method);
      },

      handleLogout() {
        this.$emit('logout');
      },

      navigateToMembers() {
        this.$emit('navigate-to-members');
      },
    },
  };
  </script>

  <style scoped>
  .subscription-manager {
    position: relative;
  }

  .account-control {
    display: flex;
    align-items: center;
  }

  .status-badges {
    margin-left: 8px;
    display: flex;
    align-items: center;
  }

  .status-badge {
    margin-left: 4px;
    cursor: pointer;
  }
  </style>
